/**
 * One Google review the client has ALREADY answered, with the reply they sent.
 *
 * Read-only on purpose. ReviewReplyCard exists to draft an answer; offering
 * "Draft a reply" on a review that already has one would invite a second,
 * contradictory public reply under the client's name.
 *
 * This exists because the audit counted answered reviews for months and never
 * showed one. An operator went looking for a Kremer Dental review the client
 * had responded to, could not find it anywhere on the page, and reported the
 * audit as broken (2026-09-18).
 */
import { Star } from "lucide-react";

import type { GbpAnsweredReview } from "@/services/gbpAuditService";

interface Props {
	review: GbpAnsweredReview;
}

/**
 * What Google says about a reply, in words rather than an enum.
 *
 * `null` for an unknown state, which includes the common case of a reading
 * source that does not report one. A reply waiting on Google's moderation and
 * a reply live on Google must never look the same, so silence is the only
 * honest rendering of "we do not know".
 */
const replyStatus = (
	state: string,
): { label: string; hint: string; tone: string } | null => {
	switch (state) {
		case "PENDING":
			return {
				label: "Waiting on Google",
				hint: "Google checks owner replies before showing them — usually minutes, occasionally up to 30 days.",
				tone: "text-[hsl(var(--status-warning))]",
			};
		case "APPROVED":
			return {
				label: "Published",
				hint: "Live on Google.",
				tone: "text-[hsl(var(--status-success))]",
			};
		case "REJECTED":
			return {
				label: "Rejected by Google",
				hint: "Google refused this reply, so nobody can see it. Edit it and send again.",
				tone: "text-destructive",
			};
		default:
			return null;
	}
};

const formatDate = (value: string): string => {
	if (!value) return "";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime())
		? value
		: parsed.toLocaleDateString(undefined, {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
};

export default function AnsweredReviewCard({ review }: Props) {
	const rating = review.rating ?? 0;
	const status = replyStatus(review.replyState);

	return (
		<div
			className="rounded-lg border border-border p-4 space-y-3"
			data-testid="gbp-answered-review-card"
		>
			<div className="min-w-0">
				<div className="flex items-center gap-2 flex-wrap">
					<span className="inline-flex items-center gap-0.5">
						{[1, 2, 3, 4, 5].map((n) => (
							<Star
								key={n}
								className={
									n <= rating
										? "h-3.5 w-3.5 fill-current text-[hsl(var(--status-warning))]"
										: "h-3.5 w-3.5 text-muted-foreground/40"
								}
							/>
						))}
					</span>
					{review.reviewer && (
						<span className="text-sm font-medium truncate">
							{review.reviewer}
						</span>
					)}
					{review.dateCreated && (
						<span className="text-xs text-muted-foreground">
							{formatDate(review.dateCreated)}
						</span>
					)}
				</div>
				{review.comment ? (
					<p className="mt-2 text-sm text-foreground/90 whitespace-pre-line">
						{review.comment}
					</p>
				) : (
					<p className="mt-2 text-sm italic text-muted-foreground">
						Rating only — the reviewer left no comment.
					</p>
				)}
			</div>

			<div className="rounded-md border-l-2 border-coral bg-muted/50 pl-3 py-2">
				<div className="flex items-center gap-2 flex-wrap">
					<p className="text-xs font-medium text-muted-foreground">
						Replied
						{review.replyDate ? ` ${formatDate(review.replyDate)}` : ""}
					</p>
					{status && (
						<span
							className={`text-xs font-medium ${status.tone}`}
							title={status.hint}
							data-testid="gbp-answered-reply-state"
						>
							{status.label}
						</span>
					)}
				</div>
				{review.reply ? (
					<p
						className="mt-1 text-sm text-foreground/90 whitespace-pre-line"
						data-testid="gbp-answered-review-reply"
					>
						{review.reply}
					</p>
				) : (
					/* Marked as answered by Google, but the text did not reach us.
					   Say that, rather than render an empty box that reads as an
					   empty reply. */
					<p className="mt-1 text-sm italic text-muted-foreground">
						A reply was sent, but its text wasn't returned.
					</p>
				)}
			</div>
		</div>
	);
}
